import { useState } from 'react';
import { useEscapeToClose, useFocusTrap } from '@/lib/use-escape';

/**
 * 자막 추가 모달 (project rule: never use native window.prompt/confirm).
 *
 * 여기서 추가하는 자막은 항상 **강조 자막(caption-2)** 이다. 발화 자막(caption-1)은
 * 전사에서 기계가 만들고 컷이 바뀌면 통째로 재생성되므로, 사람이 거기에 쓴 문구는
 * 예고 없이 사라진다 — 그래서 사람 입력은 caption-2 한 곳으로만 받는다
 * (docs/shorts-editor-quality-redesign.md §5 P0-2).
 */
export function CaptionModal({
  defaultDurationFrames,
  onSubmit,
  onClose,
}: {
  defaultDurationFrames: number;
  onSubmit: (text: string, durationInFrames: number) => void;
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [text, setText] = useState('');
  const [seconds, setSeconds] = useState((defaultDurationFrames / 30).toFixed(1));

  const submit = () => {
    const trimmed = text.trim();
    const dur = Math.max(1, Math.round(parseFloat(seconds) * 30));
    if (!trimmed || !Number.isFinite(dur)) return;
    onSubmit(trimmed, dur);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      ref={trapRef}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[380px] rounded-lg border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-sm font-semibold">강조 자막 추가</h2>
        <p className="mb-3 text-[11px] leading-snug text-muted-foreground">
          발화 자막은 전사에서 자동으로 만들어집니다. 여기서는 화면 위쪽에 얹는 문구를 넣습니다.
        </p>

        <label className="mb-1 block text-xs text-muted-foreground">텍스트</label>
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className="mb-3 w-full rounded-md border bg-background px-2 py-1.5 text-sm"
          placeholder="자막 내용"
        />
        <label className="mb-1 block text-xs text-muted-foreground">지속 시간 (초)</label>
        <input
          type="number"
          step="0.5"
          min="0.5"
          value={seconds}
          onChange={(e) => setSeconds(e.target.value)}
          className="mb-4 w-full rounded-md border bg-background px-2 py-1.5 text-sm"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm">
            취소
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            추가
          </button>
        </div>
      </div>
    </div>
  );
}
